import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useState } from 'react'
import { clearLocalSession, refreshSession } from '../services/api'
import { setAccessToken } from '../services/authToken'

const TOKEN_API = '@CANNECT_TOKEN_API'

const TokenApiContext = createContext({} as TokenApiData)

// undefined = bootstrap de sessão em andamento; null = sem sessão. Os guards
// de rota dependem dessa distinção para não expulsar o usuário no F5.
type TokenApi = string | null | undefined

interface TokenApiData {
  tokenApi: TokenApi
  setTokenApi: Dispatch<SetStateAction<TokenApi>>
  deleteTokenApi: () => void
}

export function TokenApiProvider({ children }: { children: ReactNode }) {
  const [tokenApi, setTokenApi] = useState<TokenApi>(undefined)

  useEffect(() => {
    // Sessões anteriores à migração deixaram um access de 24h no storage; sem
    // esta limpeza ele sobrevive ao deploy e vira sessão fantasma.
    localStorage.removeItem(TOKEN_API)

    // Dica de SSO (api-cannect) OU marcador local de login anterior — visitante
    // anônimo (maioria do e-commerce) não deve gastar rate limit com 401.
    // Nome do cookie por ambiente segue o padrão já usado para VITE_APP_ENV
    // (App.tsx:18, SEO/index.tsx:8): só 'production' é produção, o resto é dev.
    const ssoHintCookieName =
      import.meta.env.VITE_APP_ENV === 'production' ? 'cannect_sso_hint' : 'cannect_sso_hint_dev'
    const hasSsoHint = document.cookie.split('; ').some((cookie) => cookie.startsWith(`${ssoHintCookieName}=`))

    if (!hasSsoHint && !localStorage.getItem('@CANNECT_USER')) {
      setTokenApi(null)
      return
    }

    // Retry único: abas abertas juntas disparam refresh com o mesmo cookie e
    // a perdedora da corrida toma 401; na segunda tentativa ela já usa o
    // cookie rotacionado pela vencedora.
    refreshSession()
      .then((token) => setTokenApi(token))
      .catch(() =>
        new Promise((resolve) => {
          setTimeout(resolve, 1200)
        })
          .then(() => refreshSession())
          .then((token) => setTokenApi(token))
          .catch(() => setTokenApi(null))
      )
  }, [])

  useEffect(() => {
    // Mantém a fonte que o axios lê (memória) em sincronia com o estado React.
    if (tokenApi !== undefined) {
      setAccessToken(tokenApi)
    }
  }, [tokenApi])

  function deleteTokenApi() {
    clearLocalSession()
    localStorage.removeItem('@CANNECT_SIGNED_USER')

    setTokenApi(null)
  }

  return (
    <TokenApiContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        tokenApi,
        setTokenApi,
        deleteTokenApi
      }}>
      {children}
    </TokenApiContext.Provider>
  )
}

export function useTokenApi(): TokenApiData {
  return useContext(TokenApiContext)
}
